import React from "react";
import { MapPin, Users, Building, TrendingUp } from "lucide-react";
import { z } from "zod";

const RegionSchema = z.object({
	name: z.string(),
	founders: z.number(),
	companies: z.number(),
	color: z.string(),
});

const HotspotSchema = z.object({
	city: z.string(),
	count: z.number(),
	top: z.string(),
	left: z.string(),
});

const StatsSchema = z.object({
	activeFounders: z.string(),
	countries: z.string(),
});

const GlobalPresenceSchema = z.object({
	badgeText: z.string().default("Global Network"),
	header: z.string().default("Founders Worldwide"),
	highlightText: z.string().default("Building the Future"),
	description: z
		.string()
		.default(
			"Our community spans across continents, connecting PropTech innovators from Silicon Valley to Singapore, London to Lagos."
		),
	mapTitle: z.string().default("Global Founder Distribution"),
	hoverText: z.string().default("Hover over dots to see city details"),
	foundersLabel: z.string().default("founders"),
	companiesLabel: z.string().default("companies"),
	activeFoundersLabel: z.string().default("Active Founders"),
	countriesLabel: z.string().default("Countries"),
	regionalBreakdownTitle: z.string().default("Regional Breakdown"),
	joinRegionTitle: z.string().default("Join Founders in Your Region"),
	joinRegionDescription: z
		.string()
		.default(
			"Connect with PropTech innovators in your city or expand your network globally. Our platform makes regional and international connections seamless."
		),
	findLocalButtonText: z.string().default("Find Local Founders"),
	exploreGlobalButtonText: z.string().default("Explore Global Network"),
	stats: StatsSchema.default({
		activeFounders: "500+",
		countries: "50+",
	}),
	regions: z.array(RegionSchema).default([
		{ name: "North America", founders: 156, companies: 89, color: "bg-blue-500" },
		{ name: "Europe", founders: 142, companies: 78, color: "bg-teal-500" },
		{ name: "Asia Pacific", founders: 98, companies: 54, color: "bg-orange-500" },
		{ name: "Latin America", founders: 47, companies: 28, color: "bg-green-500" },
		{ name: "Middle East & Africa", founders: 23, companies: 15, color: "bg-purple-500" },
	]),
	hotspots: z.array(HotspotSchema).default([
		{ city: "San Francisco", count: 45, top: "20%", left: "8%" },
		{ city: "London", count: 38, top: "25%", left: "48%" },
		{ city: "Singapore", count: 32, top: "55%", left: "77%" },
		{ city: "New York", count: 29, top: "28%", left: "18%" },
		{ city: "Berlin", count: 26, top: "22%", left: "52%" },
		{ city: "Sydney", count: 21, top: "75%", left: "85%" },
	]),
});

type GlobalPresenceProps = z.input<typeof GlobalPresenceSchema>;

const GlobalPresence: React.FC<GlobalPresenceProps> = (props = {}) => {
	const data = GlobalPresenceSchema.parse(props);

	return (
		<section className="bg-gray-900 py-24 text-white">
			<div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
				<div className="mb-16 text-center">
					<span className="mb-4 inline-flex items-center rounded-full bg-blue-100 px-4 py-2 text-sm font-medium text-blue-800">
						<MapPin className="mr-2 h-4 w-4" />
						{data.badgeText}
					</span>
					<h2 className="mb-6 text-3xl font-bold md:text-5xl">
						{data.header}
						<span className="block bg-gradient-to-r from-blue-400 to-teal-400 bg-clip-text text-transparent">
							{data.highlightText}
						</span>
					</h2>
					<p className="mx-auto max-w-3xl text-xl leading-relaxed text-gray-300">
						{data.description}
					</p>
				</div>

				<div className="mb-16 grid items-center gap-12 lg:grid-cols-2">
					<div>
						<div className="relative overflow-hidden rounded-3xl bg-gray-800 p-8">
							<div className="absolute inset-0 bg-gradient-to-r from-blue-500/10 to-teal-500/10"></div>
							<div className="relative">
								<h3 className="mb-8 text-center text-2xl font-bold">{data.mapTitle}</h3>

								{/* Simplified World Map Representation */}
								<div className="relative mb-8 h-64 overflow-hidden rounded-2xl bg-gray-700">
									<div className="absolute inset-0 bg-gradient-to-br from-blue-500/20 to-teal-500/20"></div>

									{/* Hotspot markers */}
									{data.hotspots.map((hotspot, index) => (
										<div
											key={index}
											className="group absolute -translate-x-1/2 -translate-y-1/2 transform cursor-pointer"
											style={{ top: hotspot.top, left: hotspot.left }}
										>
											<div className="relative h-4 w-4 animate-pulse rounded-full bg-orange-500">
												<div className="absolute inset-0 animate-ping rounded-full bg-orange-500"></div>
											</div>
											<div className="absolute left-1/2 top-6 -translate-x-1/2 transform whitespace-nowrap rounded-lg bg-black/80 px-3 py-1 text-sm text-white opacity-0 transition-opacity group-hover:opacity-100">
												<div className="font-semibold">{hotspot.city}</div>
												<div className="text-xs text-gray-300">
													{hotspot.count} {data.foundersLabel}
												</div>
											</div>
										</div>
									))}

									<div className="absolute bottom-4 left-4 text-xs text-gray-400">
										{data.hoverText}
									</div>
								</div>

								<div className="text-center">
									<div className="grid grid-cols-2 gap-4">
										<div>
											<div className="mb-1 text-3xl font-bold text-blue-400">
												{data.stats.activeFounders}
											</div>
											<div className="text-gray-400">{data.activeFoundersLabel}</div>
										</div>
										<div>
											<div className="mb-1 text-3xl font-bold text-teal-400">
												{data.stats.countries}
											</div>
											<div className="text-gray-400">{data.countriesLabel}</div>
										</div>
									</div>
								</div>
							</div>
						</div>
					</div>

					<div className="space-y-4">
						<h3 className="mb-8 text-2xl font-bold">{data.regionalBreakdownTitle}</h3>
						{data.regions.map((region, index) => (
							<div
								key={index}
								className="hover:bg-gray-750 rounded-xl bg-gray-800 p-6 transition-colors"
							>
								<div className="mb-4 flex items-center justify-between">
									<div className="flex items-center">
										<div className={`h-4 w-4 ${region.color} mr-4 rounded-full`}></div>
										<h4 className="text-lg font-semibold">{region.name}</h4>
									</div>
									<TrendingUp className="h-5 w-5 text-green-400" />
								</div>
								<div className="grid grid-cols-2 gap-4">
									<div className="flex items-center">
										<Users className="mr-2 h-4 w-4 text-gray-400" />
										<span className="text-2xl font-bold text-white">{region.founders}</span>
										<span className="ml-2 text-gray-400">{data.foundersLabel}</span>
									</div>
									<div className="flex items-center">
										<Building className="mr-2 h-4 w-4 text-gray-400" />
										<span className="text-2xl font-bold text-white">{region.companies}</span>
										<span className="ml-2 text-gray-400">{data.companiesLabel}</span>
									</div>
								</div>
							</div>
						))}
					</div>
				</div>

				<div className="rounded-3xl bg-gradient-to-r from-blue-800 to-teal-800 p-8 md:p-12">
					<div className="text-center">
						<h3 className="mb-6 text-2xl font-bold md:text-3xl">{data.joinRegionTitle}</h3>
						<p className="mx-auto mb-8 max-w-2xl text-lg text-blue-100">
							{data.joinRegionDescription}
						</p>
						<div className="flex flex-col justify-center gap-4 sm:flex-row">
							<button className="rounded-full bg-orange-600 px-8 py-4 font-semibold text-white transition-all duration-200 hover:bg-orange-700 hover:shadow-lg">
								{data.findLocalButtonText}
							</button>
							<button className="rounded-full border-2 border-white/30 px-8 py-4 font-semibold text-white backdrop-blur-sm transition-all duration-200 hover:bg-white/10">
								{data.exploreGlobalButtonText}
							</button>
						</div>
					</div>
				</div>
			</div>
		</section>
	);
};

export default GlobalPresence;
